/**
 * Template interpolation for tool status messages.
 *
 * Ported from the Rain UI (`src/shared/lib/template.ts`) so both apps render
 * `status_template` identically. Supports two placeholder syntaxes:
 * - `${arg}` — curly-brace syntax
 * - `$arg`   — bare dollar syntax (word boundary)
 *
 * The formatter is intentionally dumb: arguments are expected to already be
 * decoded into display-ready primitives (see `rainv1ValueToJs`, which flattens
 * artifact references to their URI / filename string). Objects only reach the
 * JSON fallback when a tool genuinely passes a structured argument.
 */

/**
 * Interpolate a status template with the provided arguments. Missing variables
 * collapse to an empty string.
 *
 * @example
 * interpolateTemplate('Reading <${uri}>', { uri: 'grimoire://aop.spy' })
 * // → 'Reading <grimoire://aop.spy>'
 */
export function interpolateTemplate(
  template: string,
  args: Record<string, unknown>,
): string {
  if (!template || !args) {
    return template || '';
  }

  return template
    .replace(/\$\{(\w+)\}/g, (_match, key: string) => replaceArg(args[key]))
    .replace(/\$(\w+)(?=\W|$)/g, (_match, key: string) =>
      replaceArg(args[key]),
    );
}

/** Missing values become empty strings; everything else is formatted. */
const replaceArg = (value: unknown): string =>
  value === undefined || value === null ? '' : formatValueToStr(value);

/** Format a single argument value for display in the interpolated string. */
function formatValueToStr(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  // Objects/arrays: stringify, truncating long payloads.
  try {
    const str = JSON.stringify(value);
    return str.length > 50 ? `${str.slice(0, 47)}...` : str;
  } catch {
    return String(value);
  }
}
